/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description The single set of skin/UI choices Smart Tag can resolve to for a given
 * session. Centralizes a decision that used to be computed independently — and could drift —
 * in `TagView.playerType`, `AviaDependencyManager`, `TagUtilities`, and `TagModel`. See
 * `SkinModeResolver.ts` for how one of these gets chosen.
 */
export enum SkinMode {

	/**
	 * @description Smart Tag's own desktop Skin (`src/skin/`). Player stays chromeless — Skin
	 * renders all UI on top of it.
	 */
	DESKTOP = 'DESKTOP',

	/**
	 * @description avia-js-ui. On mobile web, `TagModel` also loads the Mobile Web UI Tag
	 * plugin alongside this — a per-partner customization layer over avia-js-ui's own controls
	 * — but that's a separate, independent decision (`skinMode === AVIA_UI && !isDesktop()`),
	 * not a distinct `SkinMode` value of its own.
	 */
	AVIA_UI = 'AVIA_UI',

	/**
	 * @description The standalone custom Mobile Skin (`src/skin_mobile/`), running without
	 * avia-js-ui. Player stays chromeless. Deliberately never a computed default yet — see
	 * `SkinModeResolver.ts` — reachable only via explicit override until the custom skin is
	 * built out and product decides to flip real traffic to it.
	 */
	CUSTOM_MOBILE = 'CUSTOM_MOBILE',

	/**
	 * @description No Smart Tag skin at all — fully chromeless, nothing loads on top of the
	 * player. Used by hosts with their own UI (e.g. CTV apps) that explicitly set
	 * `SKIN.enabled: false`.
	 */
	NONE = 'NONE',

}